import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Button, CircularProgress, Divider, Grid, Paper, Typography, Alert, Stack, Chip,
} from '@mui/material';
import {
  ArrowBack, Business, AccountBalance, Payments, ReceiptLong, CheckCircle,
} from '@mui/icons-material';
import { collectionsService, CustomerReceiptDetail } from '../../services/collectionsService';
import { formatIndianNumber } from '../../utils/currency';

const fmtDate = (d: any) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

const SectionCard: React.FC<{ title: string; icon: React.ReactNode; children: React.ReactNode }> = ({ title, icon, children }) => (
  <Paper variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
    <Box sx={{ px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', gap: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
      {icon}
      <Typography sx={{ fontWeight: 700, fontSize: 14 }}>{title}</Typography>
    </Box>
    <Box sx={{ p: 2.5 }}>{children}</Box>
  </Paper>
);

const KV: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Grid size={{ xs: 6, sm: 4 }}>
    <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
    <Typography variant="body2" sx={{ fontWeight: 600 }}>{value}</Typography>
  </Grid>
);

const CustomerPaymentDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [receipt, setReceipt] = useState<CustomerReceiptDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const { receipt: r } = await collectionsService.getReceipt(Number(id));
      setReceipt(r);
      setError(null);
    } catch (e: any) {
      setError(e.message || 'Failed to load receipt');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>;
  if (error && !receipt) return <Box sx={{ p: 3 }}><Alert severity="error" action={<Button onClick={load}>Retry</Button>}>{error}</Alert></Box>;
  if (!receipt) return null;

  return (
    <Box sx={{ p: 3 }}>
      <Button size="small" startIcon={<ArrowBack />} onClick={() => navigate('/finance/customer-payments')} sx={{ mb: 1.5 }}>
        Customer Payments
      </Button>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Header band */}
      <Paper variant="outlined" sx={{ borderRadius: 2, p: 2.5, mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
          <Box>
            <Typography sx={{ fontFamily: 'monospace', fontWeight: 800, fontSize: 20 }}>{receipt.receiptNumber}</Typography>
            <Typography variant="body2" color="text.secondary">
              {receipt.customer.name}{receipt.invoice.invoiceNumber ? ` · against ${receipt.invoice.invoiceNumber}` : ''}
            </Typography>
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="caption" color="text.secondary">Amount Received</Typography>
            <Typography sx={{ fontWeight: 800, fontSize: 18 }}>{formatIndianNumber(receipt.amount)}</Typography>
          </Box>
          <Stack spacing={0.75} alignItems="flex-end">
            <Chip label={(receipt.paymentMode || '').replace(/_/g, ' ')} size="small" variant="outlined" />
            <Typography variant="caption" color="text.secondary">{fmtDate(receipt.paymentDate)}</Typography>
          </Stack>
        </Box>
        <Divider sx={{ my: 1.5 }} />
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {receipt.invoice.id && (
            <Button variant="text" size="small" startIcon={<ReceiptLong />} onClick={() => navigate(`/sales/invoices/${receipt.invoice.id}`)}>
              View Invoice {receipt.invoice.invoiceNumber || ''}
            </Button>
          )}
          {receipt.customer.id && (
            <Button variant="text" size="small" startIcon={<Business />} onClick={() => navigate(`/entity/customer/${receipt.customer.id}`)}>
              View Customer
            </Button>
          )}
        </Stack>
      </Paper>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 7 }}>
          {/* Receipt info */}
          <SectionCard title="Receipt Information" icon={<Payments fontSize="small" color="primary" />}>
            <Grid container spacing={2}>
              <KV label="Customer" value={receipt.customer.name || '—'} />
              <KV label="Payment Date" value={fmtDate(receipt.paymentDate)} />
              <KV label="Payment Mode" value={String(receipt.paymentMode || '—').replace(/_/g, ' ')} />
              <KV label="Reference" value={receipt.reference || '—'} />
              <KV label="Customer GSTIN" value={receipt.customer.gstin || '—'} />
              <KV label="Amount" value={<Box component="span" sx={{ fontWeight: 800 }}>{formatIndianNumber(receipt.amount)}</Box>} />
            </Grid>
            {receipt.notes && <><Divider sx={{ my: 2 }} /><Typography variant="caption" color="text.secondary" display="block">Notes</Typography><Typography variant="body2">{receipt.notes}</Typography></>}
          </SectionCard>

          {/* Invoice */}
          <SectionCard title="Applied To Invoice" icon={<ReceiptLong fontSize="small" color="primary" />}>
            <Grid container spacing={2}>
              <KV
                label="Invoice"
                value={receipt.invoice.id
                  ? (
                    <Box
                      component="span"
                      onClick={() => navigate(`/sales/invoices/${receipt.invoice.id}`)}
                      sx={{ color: 'primary.main', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                    >
                      {receipt.invoice.invoiceNumber}
                    </Box>
                  )
                  : (receipt.invoice.invoiceNumber || '—')}
              />
              <KV label="Invoice Date" value={fmtDate(receipt.invoice.invoiceDate)} />
              <KV label="Status" value={receipt.invoice.status || '—'} />
              <KV label="Total Amount" value={formatIndianNumber(receipt.invoice.totalAmount)} />
              <KV label="Paid To Date" value={formatIndianNumber(receipt.invoice.paidAmount)} />
              <KV label="Outstanding Balance" value={formatIndianNumber(receipt.invoice.balance)} />
            </Grid>
          </SectionCard>
        </Grid>

        <Grid size={{ xs: 12, md: 5 }}>
          {/* Accounting */}
          <SectionCard title="Accounting" icon={<AccountBalance fontSize="small" color="primary" />}>
            {receipt.gl ? (
              <>
                <Alert severity="success" icon={<CheckCircle fontSize="inherit" />} sx={{ mb: 1.5 }}>
                  Posted to the ledger — Entry {receipt.gl.entryNumber} · {fmtDate(receipt.gl.entryDate)}.
                  <Button size="small" sx={{ ml: 1 }} onClick={() => navigate(`/finance/journal/${receipt.gl!.journalEntryId}`)}>View Journal</Button>
                </Alert>
                <Stack spacing={1}>
                  {receipt.gl.lines.map((l, i) => (
                    <Stack key={i} direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 0.5, borderBottom: i < receipt.gl!.lines.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{l.accountName}</Typography>
                        <Typography variant="caption" color="text.secondary">{l.accountCode}</Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        {l.debit > 0 && <Typography variant="body2" sx={{ fontWeight: 700 }}>Dr {formatIndianNumber(l.debit)}</Typography>}
                        {l.credit > 0 && <Typography variant="body2" sx={{ fontWeight: 700 }}>Cr {formatIndianNumber(l.credit)}</Typography>}
                      </Box>
                    </Stack>
                  ))}
                </Stack>
              </>
            ) : (
              <Alert severity="info">No journal entry linked to this receipt.</Alert>
            )}
          </SectionCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CustomerPaymentDetailPage;
